import { useCallback, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { useUser } from '@clerk/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Cropper, { type Area } from 'react-easy-crop';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';

interface UserProfile {
  tutorProfile: { calendlyUrl: string | null } | null;
}

type FeedbackTone = 'success' | 'error';
interface Feedback {
  tone: FeedbackTone;
  message: string;
}

const MAX_AVATAR_MB = Number(import.meta.env.VITE_MAX_AVATAR_MB ?? 5);
const MAX_AVATAR_BYTES = MAX_AVATAR_MB * 1024 * 1024;
const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg'];

export default function SettingsPage() {
  const { user } = useAuth();
  const { user: clerkUser, isLoaded: clerkLoaded } = useUser();
  const queryClient = useQueryClient();
  const isTutor = user?.role === 'TUTOR';

  return (
    <div className="mx-auto flex-1 max-w-7xl space-y-8 overflow-y-auto px-4 py-6 sm:px-6 sm:py-8 lg:p-12">
      <div className="space-y-8">
        <header>
          <p className="text-[10px] uppercase tracking-[0.2em] text-primary font-bold mb-2">
            Account
          </p>
          <h1 className="text-3xl font-bold text-on-surface">Settings</h1>
          <p className="text-sm text-on-surface-variant mt-2">
            Manage your profile information and how others see you on the platform.
          </p>
        </header>

        <ProfileSection clerkUser={clerkUser} clerkLoaded={clerkLoaded} />

        <AccountSection email={user?.email ?? ''} role={user?.role ?? ''} />

        {isTutor && (
          <TutorCalendlySection queryClient={queryClient} />
        )}
      </div>
    </div>
  );
}

interface ProfileSectionProps {
  readonly clerkUser: ReturnType<typeof useUser>['user'];
  readonly clerkLoaded: boolean;
}

function ProfileSection({ clerkUser, clerkLoaded }: ProfileSectionProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [initialized, setInitialized] = useState(false);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
  const [cropSource, setCropSource] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (clerkLoaded && clerkUser && !initialized) {
    setFirstName(clerkUser.firstName ?? '');
    setLastName(clerkUser.lastName ?? '');
    setInitialized(true);
  }

  const showFeedback = (tone: FeedbackTone, message: string) => {
    setFeedback({ tone, message });
    setTimeout(() => setFeedback(null), 3500);
  };

  const dirty =
    initialized &&
    clerkUser != null &&
    (firstName.trim() !== (clerkUser.firstName ?? '') ||
      lastName.trim() !== (clerkUser.lastName ?? ''));

  const handleSaveProfile = async (e: FormEvent) => {
    e.preventDefault();
    if (!clerkUser) return;
    if (!firstName.trim()) {
      showFeedback('error', 'First name is required.');
      return;
    }
    setSavingProfile(true);
    try {
      await clerkUser.update({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });
      showFeedback('success', 'Profile updated.');
    } catch (err) {
      showFeedback('error', extractClerkError(err) ?? 'Could not update profile.');
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePickFile = () => fileInputRef.current?.click();

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !clerkUser) return;

    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      showFeedback('error', 'Image must be PNG, JPEG or JPG.');
      return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      showFeedback('error', `Image must be smaller than ${MAX_AVATAR_MB} MB.`);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') setCropSource(reader.result);
    };
    reader.onerror = () => showFeedback('error', 'Could not read image.');
    reader.readAsDataURL(file);
  };

  const handleCropConfirm = async (croppedBlob: Blob) => {
    if (!clerkUser) return;
    setCropSource(null);
    setUploadingAvatar(true);
    try {
      const file = new File([croppedBlob], 'avatar.png', { type: 'image/png' });
      await clerkUser.setProfileImage({ file });
      showFeedback('success', 'Profile picture updated.');
    } catch (err) {
      showFeedback('error', extractClerkError(err) ?? 'Could not upload image.');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleRemoveAvatar = async () => {
    if (!clerkUser) return;
    setConfirmRemoveOpen(false);
    setUploadingAvatar(true);
    try {
      await clerkUser.setProfileImage({ file: null });
      showFeedback('success', 'Profile picture removed.');
    } catch (err) {
      showFeedback('error', extractClerkError(err) ?? 'Could not remove image.');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const avatarUrl = clerkUser?.imageUrl;
  const hasCustomImage = clerkUser?.hasImage ?? false;
  const initials = getInitials(clerkUser?.firstName, clerkUser?.lastName, clerkUser?.primaryEmailAddress?.emailAddress);

  return (
    <Section
      icon="person"
      iconTone="primary"
      title="Profile"
      description="This is how your name and picture appear across the platform."
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-5">
        <div className="relative shrink-0">
          <div className="w-20 h-20 rounded-full overflow-hidden bg-surface-container-high border border-outline-variant/20 flex items-center justify-center">
            {avatarUrl ? (
              <img src={avatarUrl} alt="Profile" className="w-full h-full object-cover" />
            ) : (
              <span className="text-2xl font-semibold text-on-surface-variant">{initials}</span>
            )}
          </div>
          {uploadingAvatar && (
            <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center">
              <span className="material-symbols-outlined animate-spin text-white text-lg">progress_activity</span>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_IMAGE_TYPES.join(',')}
            onChange={handleFileChange}
            className="hidden"
          />
          <button
            type="button"
            onClick={handlePickFile}
            disabled={uploadingAvatar || !clerkLoaded}
            className="px-3 py-1.5 rounded-md bg-surface-container-high text-on-surface text-sm font-medium hover:bg-surface-container-highest transition-colors disabled:opacity-40"
          >
            {hasCustomImage ? 'Change picture' : 'Upload picture'}
          </button>
          {hasCustomImage && (
            <button
              type="button"
              onClick={() => setConfirmRemoveOpen(true)}
              disabled={uploadingAvatar}
              className="px-3 py-1.5 rounded-md text-sm text-on-surface-variant hover:text-on-surface hover:bg-surface-container transition-colors disabled:opacity-40"
            >
              Remove
            </button>
          )}
          <p className="basis-full text-[11px] text-on-surface-variant">
            PNG, JPEG or JPG. Max {MAX_AVATAR_MB} MB.
          </p>
        </div>
      </div>

      <form onSubmit={handleSaveProfile} className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
        <Field label="First name" htmlFor="firstName">
          <input
            id="firstName"
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            disabled={!clerkLoaded}
            placeholder="Your first name"
            className="input-field"
          />
        </Field>
        <Field label="Last name" htmlFor="lastName">
          <input
            id="lastName"
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            disabled={!clerkLoaded}
            placeholder="Your last name"
            className="input-field"
          />
        </Field>

        <div className="sm:col-span-2 flex items-center gap-3 pt-1">
          <button
            type="submit"
            disabled={savingProfile || !dirty || !clerkLoaded}
            className="px-4 py-2 rounded-md primary-gradient text-on-primary font-semibold text-sm disabled:opacity-40"
          >
            {savingProfile ? 'Saving…' : 'Save changes'}
          </button>
          <FeedbackPill feedback={feedback} />
        </div>
      </form>

      {cropSource && (
        <CropAvatarDialog
          source={cropSource}
          onCancel={() => setCropSource(null)}
          onConfirm={handleCropConfirm}
        />
      )}

      <ConfirmDialog
        open={confirmRemoveOpen}
        title="Remove profile picture?"
        description="Your picture will be replaced with your initials. You can upload a new one anytime."
        confirmLabel="Remove"
        tone="danger"
        onConfirm={handleRemoveAvatar}
        onCancel={() => setConfirmRemoveOpen(false)}
      />
    </Section>
  );
}

function AccountSection({ email, role }: { readonly email: string; readonly role: string }) {
  return (
    <Section
      icon="badge"
      iconTone="info"
      title="Account"
      description="Sign-in details linked to your account."
    >
      <dl className="grid grid-cols-1 sm:grid-cols-[120px_1fr] gap-y-3 gap-x-4 text-sm">
        <dt className="text-on-surface-variant">Email</dt>
        <dd className="text-on-surface flex items-center gap-2 flex-wrap">
          <span>{email}</span>
          <span className="text-[10px] uppercase tracking-wider text-on-surface-variant bg-surface-container-high px-2 py-0.5 rounded">
            Verified
          </span>
        </dd>

        <dt className="text-on-surface-variant">Role</dt>
        <dd className="text-on-surface capitalize">{role.toLowerCase() || '—'}</dd>
      </dl>
      <p className="text-[11px] text-on-surface-variant">
        Your email is managed by your sign-in provider and can&apos;t be changed here.
      </p>
    </Section>
  );
}

interface TutorCalendlySectionProps {
  readonly queryClient: ReturnType<typeof useQueryClient>;
}

function TutorCalendlySection({ queryClient }: TutorCalendlySectionProps) {
  const { data: profile } = useQuery<UserProfile>({
    queryKey: ['my-profile'],
    queryFn: () => api.get<UserProfile>('/users/profile'),
  });
  const currentCalendlyUrl = profile?.tutorProfile?.calendlyUrl ?? '';
  const [calendlyUrl, setCalendlyUrl] = useState('');
  const [feedback, setFeedback] = useState<Feedback | null>(null);

  const showFeedback = (tone: FeedbackTone, message: string) => {
    setFeedback({ tone, message });
    setTimeout(() => setFeedback(null), 3500);
  };

  const saveMutation = useMutation({
    mutationFn: (url: string) =>
      api.put('/users/profile', { tutorProfile: { calendlyUrl: url } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-profile'] });
      queryClient.invalidateQueries({ queryKey: ['tutors-calendly'] });
      showFeedback('success', 'Calendly link saved.');
      setCalendlyUrl('');
    },
    onError: () => showFeedback('error', 'Could not save Calendly link.'),
  });

  const handleSave = (e: FormEvent) => {
    e.preventDefault();
    const value = calendlyUrl.trim() || currentCalendlyUrl;
    if (!value) {
      showFeedback('error', 'Add a Calendly URL first.');
      return;
    }
    saveMutation.mutate(value);
  };

  return (
    <Section
      icon="event_available"
      iconTone="info"
      title="Calendly"
      description="Students will see this link to book sessions with you."
    >
      <form onSubmit={handleSave} className="space-y-3">
        <input
          type="url"
          placeholder={currentCalendlyUrl || 'https://calendly.com/your-name'}
          value={calendlyUrl}
          onChange={(e) => setCalendlyUrl(e.target.value)}
          className="input-field"
        />
        <div className="flex items-center gap-3 flex-wrap">
          <button
            type="submit"
            disabled={saveMutation.isPending}
            className="px-4 py-2 rounded-md primary-gradient text-on-primary font-semibold text-sm disabled:opacity-40"
          >
            {saveMutation.isPending ? 'Saving…' : 'Save'}
          </button>
          <FeedbackPill feedback={feedback} />
          {currentCalendlyUrl && !calendlyUrl && (
            <span className="text-xs text-on-surface-variant">
              Current: <span className="text-on-surface">{currentCalendlyUrl}</span>
            </span>
          )}
        </div>
      </form>
    </Section>
  );
}

interface SectionProps {
  readonly icon: string;
  readonly iconTone: 'primary' | 'info';
  readonly title: string;
  readonly description?: string;
  readonly children: React.ReactNode;
}

function Section({ icon, iconTone, title, description, children }: SectionProps) {
  const toneClasses =
    iconTone === 'primary'
      ? 'bg-primary/10 text-primary'
      : 'bg-blue-500/10 text-blue-400';

  return (
    <section className="bg-surface-container-low border border-outline-variant/10 rounded-xl p-6 space-y-5">
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${toneClasses}`}>
          <span className="material-symbols-outlined text-xl">{icon}</span>
        </div>
        <div className="min-w-0">
          <h2 className="font-semibold text-on-surface">{title}</h2>
          {description && (
            <p className="text-xs text-on-surface-variant mt-0.5">{description}</p>
          )}
        </div>
      </div>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Field({
  label,
  htmlFor,
  children,
}: {
  readonly label: string;
  readonly htmlFor: string;
  readonly children: React.ReactNode;
}) {
  return (
    <label htmlFor={htmlFor} className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-on-surface-variant">{label}</span>
      {children}
    </label>
  );
}

function FeedbackPill({ feedback }: { readonly feedback: Feedback | null }) {
  if (!feedback) return null;
  const isSuccess = feedback.tone === 'success';
  return (
    <output
      className={`text-xs flex items-center gap-1 ${
        isSuccess ? 'text-green-400' : 'text-red-400'
      }`}
    >
      <span className="material-symbols-outlined text-sm">
        {isSuccess ? 'check_circle' : 'error'}
      </span>
      {feedback.message}
    </output>
  );
}

function getInitials(first?: string | null, last?: string | null, email?: string | null) {
  const f = (first ?? '').trim();
  const l = (last ?? '').trim();
  if (f || l) return `${f[0] ?? ''}${l[0] ?? ''}`.toUpperCase() || '?';
  return (email ?? '?').slice(0, 1).toUpperCase();
}

interface CropAvatarDialogProps {
  readonly source: string;
  readonly onConfirm: (blob: Blob) => void | Promise<void>;
  readonly onCancel: () => void;
}

const OUTPUT_SIZE = 512;

function CropAvatarDialog({ source, onConfirm, onCancel }: CropAvatarDialogProps) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedArea, setCroppedArea] = useState<Area | null>(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onCropComplete = useCallback((_: Area, pixels: Area) => {
    setCroppedArea(pixels);
  }, []);

  const handleSave = async () => {
    if (!croppedArea) return;
    setProcessing(true);
    setError(null);
    try {
      const blob = await getCroppedBlob(source, croppedArea);
      await onConfirm(blob);
    } catch {
      setError('Could not process image.');
      setProcessing(false);
    }
  };

  return (
    <dialog
      open
      aria-modal="true"
      aria-labelledby="crop-dialog-title"
      className="fixed inset-0 z-50 w-full h-full max-w-none max-h-none bg-transparent p-4 flex items-center justify-center"
    >
      <button
        type="button"
        aria-label="Cancel"
        onClick={onCancel}
        disabled={processing}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
      />
      <div className="relative bg-surface-container-low border border-outline-variant/20 rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="px-6 pt-5 pb-3">
          <h3 id="crop-dialog-title" className="font-semibold text-on-surface">
            Adjust your picture
          </h3>
          <p className="text-xs text-on-surface-variant mt-1">
            Drag to reposition, scroll or use the slider to zoom.
          </p>
        </div>

        <div className="relative h-72 bg-black">
          <Cropper
            image={source}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>

        <div className="px-6 py-4 space-y-4">
          <div className="flex items-center gap-3">
            <span className="material-symbols-outlined text-on-surface-variant text-lg">zoom_out</span>
            <input
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1 accent-primary"
              aria-label="Zoom"
            />
            <span className="material-symbols-outlined text-on-surface-variant text-lg">zoom_in</span>
          </div>

          {error && <p className="text-xs text-red-400">{error}</p>}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={processing}
              className="px-4 py-2 rounded-md text-sm font-medium text-on-surface hover:bg-surface-container transition-colors disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={processing || !croppedArea}
              className="px-4 py-2 rounded-md primary-gradient text-on-primary text-sm font-semibold disabled:opacity-40"
            >
              {processing ? 'Saving…' : 'Save picture'}
            </button>
          </div>
        </div>
      </div>
    </dialog>
  );
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Image load failed'));
    img.src = src;
  });
}

async function getCroppedBlob(source: string, area: Area): Promise<Blob> {
  const image = await loadImage(source);
  const canvas = document.createElement('canvas');
  canvas.width = OUTPUT_SIZE;
  canvas.height = OUTPUT_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas unsupported');

  ctx.drawImage(
    image,
    area.x,
    area.y,
    area.width,
    area.height,
    0,
    0,
    OUTPUT_SIZE,
    OUTPUT_SIZE,
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Blob failed'))),
      'image/png',
      0.92,
    );
  });
}

interface ConfirmDialogProps {
  readonly open: boolean;
  readonly title: string;
  readonly description?: string;
  readonly confirmLabel: string;
  readonly cancelLabel?: string;
  readonly tone?: 'danger' | 'default';
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel = 'Cancel',
  tone = 'default',
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  if (!open) return null;

  const confirmClasses =
    tone === 'danger'
      ? 'bg-red-500 text-white hover:bg-red-600'
      : 'primary-gradient text-on-primary';

  return (
    <dialog
      open
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
      className="fixed inset-0 z-50 w-full h-full max-w-none max-h-none bg-transparent p-4 flex items-center justify-center"
    >
      <button
        type="button"
        aria-label="Cancel"
        onClick={onCancel}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
      />
      <div className="relative bg-surface-container-low border border-outline-variant/20 rounded-xl shadow-2xl p-6 w-full max-w-sm space-y-4">
        <div className="flex items-start gap-3">
          {tone === 'danger' && (
            <div className="w-10 h-10 rounded-lg bg-red-500/10 text-red-400 flex items-center justify-center shrink-0">
              <span className="material-symbols-outlined text-xl">warning</span>
            </div>
          )}
          <div className="min-w-0">
            <h3 id="confirm-dialog-title" className="font-semibold text-on-surface">
              {title}
            </h3>
            {description && (
              <p className="text-sm text-on-surface-variant mt-1">{description}</p>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded-md text-sm font-medium text-on-surface hover:bg-surface-container transition-colors"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${confirmClasses}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </dialog>
  );
}

function extractClerkError(err: unknown): string | null {
  const e = err as { errors?: Array<{ longMessage?: string; message?: string }> };
  return e?.errors?.[0]?.longMessage ?? e?.errors?.[0]?.message ?? null;
}
